/**
 * QAPublishWarning - Dialog shown before publishing if QA review is incomplete
 *
 * Checks the QA review status for a content item. If no review exists or
 * there are flagged issues, shows a warning dialog with two options:
 * - "Review QA First" -> navigates to QA review page
 * - "Publish Anyway" -> proceeds with publish
 *
 * If all 13 checks pass with 0 issues, publish proceeds without warning.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ClipboardList } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { useQAReview } from '@/hooks/useQAReviews';
import { getIssueCount, QA_RULES } from '@/types/qaReview';

interface QAPublishWarningProps {
  contentId: string;
  tenantId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPublishAnyway: () => void;
}

export const QAPublishWarning: React.FC<QAPublishWarningProps> = ({
  contentId,
  tenantId,
  open,
  onOpenChange,
  onPublishAnyway,
}) => {
  const navigate = useNavigate();
  const { data: review } = useQAReview(contentId, tenantId);

  const totalRules = QA_RULES.length;
  const issueCount = review ? getIssueCount(review) : 0;
  const hasReview = !!review?.reviewed_at;

  const statusText = hasReview
    ? `${issueCount} of ${totalRules} issues flagged`
    : `Not yet reviewed (0 of ${totalRules} checks completed)`;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-500 dark:text-amber-400" />
            QA Review Incomplete
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block mt-1">{statusText}</span>
            <span className="block mt-2 text-muted-foreground">
              Are you sure you want to publish without completing the QA review?
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
              navigate('/content/qa');
            }}
          >
            <ClipboardList size={14} />
            Review QA First
          </button>
          <button
            className="inline-flex items-center justify-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-medium text-white hover:bg-amber-600"
            onClick={() => {
              onOpenChange(false);
              onPublishAnyway();
            }}
          >
            Publish Anyway
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/**
 * Hook that wraps publish logic with QA warning gate.
 *
 * Returns a `triggerPublish` function that either:
 * - Publishes directly if QA is clean (0 issues, reviewed)
 * - Opens the warning dialog otherwise
 *
 * Also returns `warningProps` to spread on <QAPublishWarning />.
 */
export function useQAPublishGate(contentId: string, tenantId: string, onPublish: () => void) {
  const { data: review } = useQAReview(contentId, tenantId);
  const [showWarning, setShowWarning] = React.useState(false);

  const triggerPublish = React.useCallback(() => {
    // If review exists, is complete, and has 0 issues -> publish directly
    if (review?.reviewed_at && getIssueCount(review) === 0) {
      onPublish();
      return;
    }
    // Otherwise show warning
    setShowWarning(true);
  }, [review, onPublish]);

  const warningProps = {
    contentId,
    tenantId,
    open: showWarning,
    onOpenChange: setShowWarning,
    onPublishAnyway: onPublish,
  };

  return { triggerPublish, warningProps };
}

export default QAPublishWarning;
